/**
 * TagadaPay SDK Error Types
 *
 * Structured error hierarchy for programmatic error handling.
 * All SDK errors extend TagadaError, which extends native Error.
 */

export const TagadaErrorCode = {
  NETWORK_ERROR: 'network_error',
  API_ERROR: 'api_error',
  AUTH_REQUIRED: 'auth_required',
  TOKEN_EXPIRED: 'token_expired',
  NOT_FOUND: 'not_found',
  VALIDATION_ERROR: 'validation_error',
  CIRCUIT_BREAKER: 'circuit_breaker',
  PAYMENT_FAILED: 'payment_failed',
  CARD_DECLINED: 'card_declined',
  SESSION_EXPIRED: 'session_expired',
  RATE_LIMITED: 'rate_limited',
  TIMEOUT: 'timeout',
  UNKNOWN: 'unknown',
} as const;

export type TagadaErrorCodeValue = typeof TagadaErrorCode[keyof typeof TagadaErrorCode];

export interface TagadaErrorOptions {
  code: TagadaErrorCodeValue | string;
  statusCode?: number;
  retryable?: boolean;
  details?: Record<string, unknown>;
}

/**
 * Base error class for all TagadaPay SDK errors.
 *
 * @example
 * ```typescript
 * try {
 *   await checkout.applyPromotionCode(sessionId, 'SAVE10');
 * } catch (err) {
 *   if (err instanceof TagadaError) {
 *     console.log(err.code);       // 'not_found'
 *     console.log(err.statusCode);  // 404
 *     console.log(err.retryable);   // false
 *   }
 * }
 * ```
 */
export class TagadaError extends Error {
  readonly code: string;
  readonly statusCode?: number;
  readonly retryable: boolean;
  readonly details?: Record<string, unknown>;

  constructor(message: string, options: TagadaErrorOptions) {
    super(message);
    this.name = 'TagadaError';
    this.code = options.code;
    this.statusCode = options.statusCode;
    this.retryable = options.retryable ?? false;
    this.details = options.details;

    // Maintain proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** API returned a non-2xx response with an error body. */
export class TagadaApiError extends TagadaError {
  constructor(message: string, statusCode: number, options?: { code?: string; details?: Record<string, unknown>; retryable?: boolean }) {
    super(message, {
      code: options?.code ?? TagadaErrorCode.API_ERROR,
      statusCode,
      retryable: options?.retryable ?? (statusCode >= 500),
      details: options?.details,
    });
    this.name = 'TagadaApiError';
  }
}

/** Network-level failure (no response received). */
export class TagadaNetworkError extends TagadaError {
  constructor(message: string = 'Network request failed') {
    super(message, {
      code: TagadaErrorCode.NETWORK_ERROR,
      retryable: true,
    });
    this.name = 'TagadaNetworkError';
  }
}

/** 401/403 — authentication or authorization failure. */
export class TagadaAuthError extends TagadaError {
  constructor(message: string = 'Authentication required', statusCode: number = 401) {
    super(message, {
      code: TagadaErrorCode.AUTH_REQUIRED,
      statusCode,
      retryable: false,
    });
    this.name = 'TagadaAuthError';
  }
}

/** Input validation failed (client-side or 400 from server). */
export class TagadaValidationError extends TagadaError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, {
      code: TagadaErrorCode.VALIDATION_ERROR,
      statusCode: 400,
      retryable: false,
      details,
    });
    this.name = 'TagadaValidationError';
  }
}

/** Circuit breaker tripped — too many requests in a short window. */
export class TagadaCircuitBreakerError extends TagadaError {
  constructor(message: string) {
    super(message, {
      code: TagadaErrorCode.CIRCUIT_BREAKER,
      retryable: false,
    });
    this.name = 'TagadaCircuitBreakerError';
  }
}

/**
 * The server's stable machine code for a refused request, when it sent one.
 *
 * The pay endpoints answer a domain refusal as a zsa error whose `data` holds
 * `{ code, message, ... }` — serialized to a JSON string on the wire — while
 * the top-level `code` is only the HTTP family (`CONFLICT`,
 * `UNPROCESSABLE_CONTENT`). This digs the domain code out so a storefront can
 * branch on it (e.g. `FUNNEL_SESSION_ALREADY_PAID`, #TAG-3253) instead of
 * matching the customer-facing sentence. Undefined for anything else.
 */
export function getServerErrorCode(error: unknown): string | undefined {
  if (!(error instanceof TagadaError)) return undefined;
  let payload: unknown = error.details?.data;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      return undefined;
    }
  }
  if (!payload || typeof payload !== 'object') return undefined;
  const code = (payload as { code?: unknown }).code;
  return typeof code === 'string' && code ? code : undefined;
}
